/**
 * Hook for generating AI patient summaries
 *
 * Follows the same pattern as useSendSMS:
 * - Development: Vite proxy adds API key server-side
 * - Staging/Production: Direct API Gateway call with API key
 */

import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";
import type { GeneratePatientSummaryResponse } from "@benchmark/core/types";

/** Result from useGeneratePatientSummary hook. @agentic connections */
export type UseGeneratePatientSummaryResult = {
  generateSummary: (
    patientId: string,
  ) => Promise<GeneratePatientSummaryResponse | null>;
  isGenerating: boolean;
  error: Error | null;
};

/**
 * Hook to generate AI patient summary via API.
 * @agentic connections
 */
export const useGeneratePatientSummary =
  (): UseGeneratePatientSummaryResult => {
    const medplum = useMedplum();
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const generateSummary = useCallback(
      async (
        patientId: string,
      ): Promise<GeneratePatientSummaryResponse | null> => {
        if (!patientId) {
          const err = new Error("Patient ID is required");
          setError(err);
          return null;
        }

        setIsGenerating(true);
        setError(null);

        try {
          // Ensure session is active and get a fresh access token
          const accessToken = await ensureActiveSession(medplum);

          const apiUrl = getApiEndpoint("/api/v1/patient-summary");

          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Medplum-Authorization": `Bearer ${accessToken}`,
              ...getApiAuthHeaders(),
            },
            body: JSON.stringify({ patientId }),
            signal: controller.signal,
          }).finally(() => clearTimeout(timeout));

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            let errorData: Record<string, unknown> = {};
            try {
              errorData = JSON.parse(errorText);
            } catch {
              errorData = { raw: errorText };
            }
            const errorMessage =
              (typeof errorData.error === "string" ? errorData.error : null) ||
              (typeof errorData.message === "string"
                ? errorData.message
                : null) ||
              `Failed to generate summary: ${response.status} ${response.statusText}`;
            console.error("Patient summary API error:", {
              status: response.status,
              statusText: response.statusText,
              errorData,
              errorText,
              url: apiUrl,
            });
            throw new Error(errorMessage);
          }

          const data: GeneratePatientSummaryResponse = await response.json();
          setIsGenerating(false);
          return data;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to generate summary. Please try again.";

          const errorObj = new Error(errorMessage);
          setError(errorObj);
          setIsGenerating(false);
          return null;
        }
      },
      [medplum],
    );

    return {
      generateSummary,
      isGenerating,
      error,
    };
  };
